<template>
  <div class="board-card-actions">
    <ListDropdown
      :key="card.id"
      ref="dropdown"
      :width="dropdownWidth"
      :items="settingsDropdownItems"
      @click="handleSettingsDropdownClick"
      @closeEscape="hideActionDropDown"
    />
  </div>
</template>

<script>
import ListDropdown from '@/components/widgets/ListDropdown';
import getDashedTitle from '@/utilities/getDashedTitle';
import cardArchiveMixin from '@/mixins/cardArchiveMixin';
import customIconsMixin from '@/mixins/customIconsMixin';
import watchMixin from '@/mixins/watchMixin';
import resourceActionsMixin from '@/mixins/resourceActionsMixin';
import generateLink from '@/utilities/generateLink';
import EventBus from '@/utilities/eventBus';
import styleVariables from '@/assets/scss/variables.module.scss';
import { ResourceType } from '@/types/resources';
import useGHIntegrationCheck from '@/utilities/composables/useGHIntegrationCheck';
import useCheckSprintState from '@/utilities/composables/useCheckSprintState';
import { getSprintListKey } from '@/utilities/sprintKeys';
import useConvertCardToEpic from '@/utilities/composables/useConvertCardToEpic';
import generateDropdownItem from '@/utilities/generateDropdownItem';
import useAddOrRemoveFromEpic from '@/utilities/composables/useAddOrRemoveFromEpic';
import { ref } from 'vue';
import useEpicFeature from '@/utilities/composables/useEpicFeature';

export default {
  name: 'BoardListCardActions',
  components: {
    ListDropdown,
  },
  mixins: [cardArchiveMixin, customIconsMixin, watchMixin, resourceActionsMixin],
  props: {
    card: {
      type: Object,
      required: true,
    },
    // for cases when group by is enabled
    cardListId: {
      type: [String, Number],
      required: false,
      default: '',
    },
  },
  emits: ['close-tippy'],
  setup(props) {
    const { convertCardToEpic } = useConvertCardToEpic();
    const { gitHubIntegratedObj } = useGHIntegrationCheck();
    const { cancelledOrCompletedSprint } = useCheckSprintState(props.card);
    const { addedToEpic, removeCardFromEpic, showAddToEpicPopup } = useAddOrRemoveFromEpic(
      ref(props.card)
    );
    const { epicUpgradeBadge, allowEpicInteraction } = useEpicFeature();

    return {
      convertCardToEpic,
      gitHubIntegratedObj,
      cancelledOrCompletedSprint,
      addedToEpic,
      removeCardFromEpic,
      showAddToEpicPopup,
      allowEpicInteraction,
      epicUpgradeBadge,
    };
  },
  data() {
    return {
      dropdownWidth: parseFloat(styleVariables.boardCardActionDropdownWidth) || 0,
    };
  },
  computed: {
    hasParentCard() {
      return Object.keys(this.card.parent_card || {}).length;
    },
    isEpic() {
      return this.card.type === ResourceType.Epic;
    },
    uniqueRowId() {
      return this.$store.getters.getCurrentCardUniqueRowId;
    },
    settingsDropdownItems() {
      const gitHubIntegratedObj = this.gitHubIntegratedObj;

      return [
        {
          id: 'subscribe',
          label: this.watchLabel,
          customIconName: this.watchIcon,
        },
        ...(this.isEpic
          ? []
          : [
              {
                id: this.hasParentCard ? 'changeParent' : 'addParent',
                label: this.translate(this.hasParentCard ? 'changeParentCard' : 'setParentCard'),
                customIconName: this.iconPath('parent-card'),
              },
              {
                id: 'addToEpic',
                label: this.addedToEpic
                  ? this.translate('removeCardFromProject')
                  : this.translate('addToProject'),
                customIconName: this.iconPath('box'),
                ...this.epicUpgradeBadge,
              },
              {
                id: 'rename',
                label: this.translate('rename'),
                customIconName: this.iconPath('edit'),
                shortcutKey: { text: 'R' },
              },
            ]),
        {
          id: 'changeStatus',
          label: this.translate('changeStatus'),
          customIconName: this.iconPath('status'),
          shortcutKey: { text: 'S' },
          disabled: this.cancelledOrCompletedSprint,
        },
        {
          id: 'assignees',
          label: this.translate('assignees'),
          customIconName: this.iconPath('user-plus'),
          shortcutKey: { text: 'A' },
        },
        {
          id: 'tags',
          label: this.translate('tags'),
          customIconName: this.iconPath('tag'),
          shortcutKey: { text: 'T' },
        },
        {
          id: 'startDate',
          label: this.translate('startDate'),
          customIconName: this.iconPath('calendar'),
        },
        {
          id: 'dueDate',
          label: this.translate('dueDate'),
          customIconName: this.iconPath('calendar'),
          shortcutKey: { text: 'D' },
        },
        {
          id: 'priority',
          label: this.translate('priority'),
          customIconName: this.iconPath('chevrons-up'),
        },
        ...(this.$store.getters.getEstimateSystemForProject(this.card.project_id)
          ? [
              {
                id: 'estimate',
                label: this.translate('estimate'),
                customIconName: this.iconPath('clock'),
              },
            ]
          : []),
        ...(this.isEpic
          ? []
          : [
              {
                id: 'duplicate',
                label: this.translate('duplicate'),
                customIconName: this.iconPath('copy'),
              },
              generateDropdownItem(
                'moveToBoard',
                this.translate('moveToBoard'),
                this.iconPath('arrow-right'),
                !this.sprintsEnabled
              ),
              this.sprintsEnabled &&
                generateDropdownItem(
                  'addToSprint',
                  this.translate('addToSprint'),
                  this.iconPath('sprint-completed'),
                  true
                ),
            ]),
        ...gitHubIntegratedObj,
        {
          id: 'copyLink',
          label: this.translate('copyLink'),
          separator: true,
          customIconName: this.iconPath('link-st'),
        },
        ...(this.isEpic
          ? []
          : [
              {
                id: 'convertCardToEpic',
                label: this.translate('convertCardToEpic'),
                separator: true,
                customIconName: this.iconPath('box'),
                ...this.epicUpgradeBadge,
              },
            ]),
        {
          id: 'archive',
          label: this.archiveLabel,
          customIconName: this.iconPath('archived'),
        },
        {
          id: 'delete',
          label: this.deleteLabel,
          customIconName: this.iconPath('trash'),
          deleteOrCancelType: true,
        },
      ];
    },
    currentBoard() {
      return this.$store.getters.currentBoard;
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    cardWatch() {
      return this.card.is_watching;
    },
    cardType() {
      return this.card.type || ResourceType.Card;
    },

    watchLabel() {
      return this.cardWatch ? this.translate('unsubscribe') : this.translate('subscribe');
    },

    cardArchived() {
      return this.card.archived;
    },

    archiveLabel() {
      return this.translate(this.cardArchived ? 'restoreResource' : 'archiveResource', {
        type: this.translate(this.cardType).toLowerCase(),
      });
    },

    deleteLabel() {
      return this.translate('deleteResource', {
        type: this.translate(this.cardType).toLowerCase(),
      });
    },

    watchIcon() {
      return this.cardWatch ? this.iconPath('bell-off') : this.iconPath('bell');
    },

    sprintsEnabled() {
      return this.$store.getters.getProjectById(this.card.project_id)?.sprints || false;
    },
  },
  methods: {
    openCard() {
      if (this.card.localCardId) return;
      this.$store.dispatch('setBoardViewNoDestroy', true);
      this.$store.dispatch('setDisplayedCards', [this.card.id]);
      const dashedCardTitle = getDashedTitle(this.card.title);
      this.$router.push(`/${this.teamSlug}/card-${this.card.id}-${dashedCardTitle}`);
    },
    archiveCardHandler() {
      const cardId = this.card.id;
      const listId = this.card.sprint_id
        ? getSprintListKey(this.card.project_id, this.card.sprint_id, this.card.list_id)
        : this.card.list_id;
      // save card index from list cards
      const listCards = this.$store.getters.getListCards[listId];
      const cardIndex = listCards?.findIndex((id) => id === cardId);
      this.archiveCard(cardId)
        .then(() => {
          // if card is selected card, then set next or previous card in the list as last hovered card
          if (this.$store.getters.lastHoveredCardId === cardId) {
            if (cardIndex === -1) return;

            // find id of next or previous card in the list
            const nextCardId = listCards[cardIndex + 1] || listCards[cardIndex - 1];
            this.$store.dispatch(
              'lastHoveredCardId',
              nextCardId || `${this.$cons.addCardButton}${listId}`
            );
          }
        })
        .catch(() => {
          this.$store.dispatch('setCurrentCardIdForActionEdit', cardId);
        });
    },
    hideActionDropDown() {
      this.$emit('close-tippy');
      this.$store.dispatch('setCurrentCardIdForActionEdit', '');
    },
    handleSettingsDropdownClick(item, index, event) {
      switch (item.id) {
        case 'subscribe':
          this.toggleWatch(this.cardWatch, ResourceType.Card, this.card.id);
          break;
        case 'rename':
          if (event === true) {
            this.renameCard(true);
            break;
          }
          this.renameCard();
          break;
        case 'changeStatus':
          this.changeStatusHandler();
          break;
        case 'duplicate':
          this.duplicateCard();
          break;
        case 'copyLink':
          this.copyLinkToClipboard();
          break;
        case 'convertCardToEpic':
          if (!this.allowEpicInteraction()) break;
          this.convertCardToEpic(this.card.id);
          break;
        case 'archive':
          this.archiveCardHandler();
          break;
        case 'assignees':
          this.assigneesCardHandler();
          break;
        case 'tags':
          this.tagsCardHandler();
          break;
        case 'priority':
          this.priorityCardHandler();
          break;
        case 'estimate':
          this.esimtateCardHandler();
          break;
        case 'dueDate':
          this.dueDateCardHandler();
          break;
        case 'startDate':
          this.startDateCardHandler();
          break;
        case 'copyBranchName':
          this.generateGitBranchName(this.card.id);
          break;
        case 'moveToBoard':
          this.$store.dispatch('fetchArchivedBoardsByWorkspace');
          this.$store.dispatch('setCardIdForMove', this.card.id);
          clearTimeout(this.setSearchResultsTimeout);
          this.setSearchResultsTimeout = setTimeout(() => {
            const myProjects = this.$store.getters.getProjects.map((project) => project.id);
            const boards =
              Object.values(this.$store.getters.getFlatBoards).filter(
                (board) =>
                  board.id !== this.currentBoard.id &&
                  myProjects.includes(board.project_id) &&
                  !board.orginalId
              ) || [];
            this.$store.dispatch('setSearchResults', { count: boards.length, cursor: '', boards });
            this.openSetParentCardPopup('moveToBoard', this.$constants.boardView);
          }, 200);
          break;
        case 'addToSprint':
          this.addToSprintHandler();
          break;
        case 'changeParent':
        case 'addParent':
          this.showSetParentCardModal(item.id);
          break;
        case 'delete':
          this.deleteCard(this.cardType, this.card.id);
          break;
        case 'addToEpic':
          if (!this.allowEpicInteraction()) break;
          this.addToEpicHandler();
          break;
        default:
          break;
      }
      this.clearCurrentCardUniqueRowId();
      this.hideActionDropDown();
    },

    addToEpicHandler() {
      if (this.addedToEpic) {
        this.removeCardFromEpic();
      } else {
        this.showAddToEpicPopup(this.$constants.boardView);
      }
    },

    renameCard(hideEditKeyboard = false) {
      EventBus.$emit('renameBoardCard', { id: this.card.id, hideEditKeyboard });
    },

    copyLinkToClipboard() {
      const cardType = this.card.sprint_id ? 'sprintcard' : 'card';
      const link = generateLink(cardType, this.teamSlug, this.card.id, this.card.title);
      navigator.clipboard.writeText(link);

      this.$store.dispatch('addUiNotification', {
        message: this.translate('linkCopiedToClipboard'),
        icon: 'link-st',
        duration: 4000,
      });
    },

    duplicateCard() {
      this.$store.dispatch(
        'setShowDuplicateCardPopup',
        `${this.card.id}-${this.$constants.boardView}`
      );
    },

    assigneesCardHandler() {
      this.$store.dispatch('setCurrentCardIdMemberDropdown', this.card.id);
      this.$store.dispatch('setCurrentCardMembersDropdown', this.card);
      EventBus.$emit('card:openAssignees', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    tagsCardHandler() {
      EventBus.$emit('card:openTags', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    priorityCardHandler() {
      EventBus.$emit('card:openPriority', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    esimtateCardHandler() {
      EventBus.$emit('card:openEstimate', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    dueDateCardHandler() {
      EventBus.$emit('card:openDueDate', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    startDateCardHandler() {
      EventBus.$emit('card:openStartDate', {
        cardId: this.card.id,
        listId: this.cardListId,
        paramUniqueRowId: this.uniqueRowId,
      });
    },

    showSetParentCardModal(action) {
      this.$store.dispatch('setShowSetParentCardPopup', `${action}-${this.$constants.boardView}`);
    },

    clearCurrentCardUniqueRowId() {
      if (this.uniqueRowId) {
        this.$store.dispatch('setCurrentCardUniqueRowId', '');
      }
    },

    changeStatusHandler() {
      EventBus.$emit('card:openStatus', {
        cardId: this.card.id,
        listId: this.cardListId,
        uniqueRowId: this.uniqueRowId,
      });
    },

    addToSprintHandler() {
      EventBus.$emit('card:addToSprint', {
        cardId: this.card.id,
        uniqueRowId: this.uniqueRowId,
      });
    },
  },
};
</script>

<style lang="scss">
.board-card-actions {
  display: flex;
  margin-left: auto;

  .dropdown {
    position: relative !important;
  }

  &.view-results-card-actions {
    width: 0;
    height: 0;

    .dropdown {
      position: relative !important;
    }
  }
}
</style>
